import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Alert } from '../components/ui/Alert';
import { Card } from '../components/ui/Card';
import { MockBadge } from '../components/MockBadge';
import { apiFetch } from '../lib/api';
import { ApiError, type CompleteAccessResetResponse } from '../types/api';

/** Reads `#token=...` out of the URL fragment, once, on mount. */
function readTokenFromHash(): string | null {
  const hash = window.location.hash;
  if (!hash.startsWith('#')) return null;
  const params = new URLSearchParams(hash.slice(1));
  return params.get('token');
}

function errorText(err: unknown): string {
  if (err instanceof ApiError && err.body.error === 'invalid_or_expired_token') {
    return 'The link is invalid, already used, or has expired. Ask an administrator for a new one.';
  }
  if (err instanceof ApiError && err.body.error === 'password_too_short') {
    return 'The password must be at least 12 characters.';
  }
  if (err instanceof ApiError && err.body.error === 'password_pwned') {
    return 'This password has appeared in a previous data breach. Choose a different one.';
  }
  if (err instanceof ApiError && err.status === 429) {
    return 'Too many attempts. Try again shortly.';
  }
  if (err instanceof ApiError && err.status === 413) {
    return 'The request was too large.';
  }
  return 'Something went wrong. Try again.';
}

/**
 * Public page (no `ProtectedShell` — nobody is authenticated), redeeming
 * the admin-issued access-reset link (runbook MANGLER item 4,
 * docs/decisions.md "Admin-udløst nulstilling af adgang"). The token is
 * only ever in the URL fragment (never a query string, so it never
 * reaches a server log or `Referer` header) — read once on mount and the
 * fragment is stripped from the visible URL immediately via
 * `history.replaceState`, before anything is submitted.
 *
 * Reachable at both `/reset-access` (current) and `/nulstil-adgang`
 * (legacy — old emailed links still point here; kept as an alias so they
 * keep working, see App.tsx).
 */
export function ResetAccessPage() {
  const [token, setToken] = useState<string | null>(null);
  const [tokenRead, setTokenRead] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordAgain, setPasswordAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const found = readTokenFromHash();
    setToken(found);
    setTokenRead(true);
    if (window.location.hash) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  const mismatch = passwordAgain.length > 0 && password !== passwordAgain;
  const canSubmit = Boolean(token) && password.length >= 12 && password === passwordAgain && !busy;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setError(null);
    setBusy(true);
    try {
      await apiFetch<CompleteAccessResetResponse>('/auth/reset-access', {
        method: 'POST',
        body: { token, newPassword: password },
      });
      setDone(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-4">
      <Card className="w-[min(400px,100%)] p-8 flex flex-col gap-5">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-[9px] bg-accent text-white font-bold flex items-center justify-center"
            aria-hidden="true"
          >
            N
          </div>
          <div>
            <div className="font-semibold">NoPain Newsletter</div>
            <div className="text-xs text-muted">Reset access</div>
          </div>
          <span className="ml-auto">
            <MockBadge />
          </span>
        </div>

        {error ? <Alert>{error}</Alert> : null}

        {done ? (
          <div className="flex flex-col gap-4">
            <div role="status" className="text-success-text text-sm">
              Your password has been reset.
            </div>
            <Button asChild>
              <Link to="/login">Log in and set up 2FA again</Link>
            </Button>
          </div>
        ) : !tokenRead ? null : !token ? (
          <Alert>
            The link is missing or invalid. Ask an administrator for a new one-time link.
          </Alert>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reset-password">New password</Label>
              <Input
                id="reset-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reset-password-again">Repeat new password</Label>
              <Input
                id="reset-password-again"
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                value={passwordAgain}
                onChange={(e) => setPasswordAgain(e.target.value)}
                aria-invalid={mismatch}
              />
              <div className="min-h-[18px] text-xs text-danger-text" aria-live="polite">
                {mismatch ? 'The passwords do not match.' : ''}
              </div>
            </div>
            <Button type="submit" disabled={!canSubmit} className="mt-2">
              {busy ? 'Resetting…' : 'Reset password'}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
